/**
 * USF instant search: debounced API calls (theme-owned; safe across USF app sync).
 *
 * Mirrors predictive-search policy: 1–2 chars wait 1s after typing stops; 3+ use a
 * shorter debounce; empty clears immediately; blur flushes pending search.
 *
 * IME: `updateTerm` still runs each keystroke so `:value` / `term` stay in sync; only
 * `updateResults` (API) is debounced. `compositionend` flushes pending search.
 *
 * QA: mobile `form.usf-is-inputbox`; desktop modal + `.usf-popup`; full layout
 * `usf-is--full__input`; USF instant search off → theme predictive-search.js only.
 *
 * Note: `usf-boot.js` installSearchInput still uses vendor ~180ms before `is_show`
 * (theme search boxes). That only delays opening the overlay, not instantsearch API.
 *
 * While the API call is intentionally delayed, `term` still updates immediately. USF
 * would otherwise show "no matches for {term}" from stale `result` + `isEmpty`. We set
 * `loader` + `tileclub-instant-search-pending` until `is_updated` reflects the current term
 * (see assets/usf-custom.css).
 */
(function () {
  'use strict';

  var SHORT_QUERY_DEBOUNCE_MS = 1000;
  var LONG_QUERY_DEBOUNCE_MS = 350;
  var MIN_CHARS_FOR_FAST_DEBOUNCE = 3;

  var globalEventsHooked = false;

  function refineForCompare(q) {
    if (typeof usf._refineTerm === 'function') return usf._refineTerm(q);
    return String(q == null ? '' : q)
      .trim()
      .toLocaleLowerCase()
      .replace(/[*\?]/g, '');
  }

  function setInstantSearchTypingPending(vm, active) {
    if (!vm) return;
    if (active) {
      vm.__tileclubInstantSearchTypingPending = true;
      vm.loader = true;
      if (vm.$el && vm.$el.classList) vm.$el.classList.add('tileclub-instant-search-pending');
    } else {
      vm.__tileclubInstantSearchTypingPending = false;
      if (vm.$el && vm.$el.classList) vm.$el.classList.remove('tileclub-instant-search-pending');
    }
  }

  function maybeClearInstantSearchTypingPending(inst) {
    if (!inst || inst !== usf.instantSearch) return;
    if (!inst.__tileclubInstantSearchTypingPending) return;
    if (inst.__tileclubSearchTimer != null) return;
    var r = inst.result;
    if (!r || r.query == null) return;
    if (refineForCompare(inst.term) !== refineForCompare(r.query)) return;
    setInstantSearchTypingPending(inst, false);
  }

  function trimTerm(v) {
    return String(v == null ? '' : v).trim();
  }

  function isFullInstantSearchLayout() {
    return !!(usf.settings && usf.settings.instantSearch && usf.settings.instantSearch.layout === 'full');
  }

  function cancelPending(vm) {
    if (vm.__tileclubSearchTimer != null) {
      clearTimeout(vm.__tileclubSearchTimer);
      vm.__tileclubSearchTimer = null;
    }
  }

  function syncInputDisplay(vm, value) {
    if (usf.isMobile || isFullInstantSearchLayout()) {
      if (vm.input) vm.input.value = value;
    }
  }

  function installOnInstance(vm) {
    if (!vm || typeof vm.updateResults !== 'function' || typeof vm.updateTerm !== 'function') return;

    var origUpdateResults = vm.updateResults.bind(vm);

    function flushUpdateResults() {
      cancelPending(vm);
      origUpdateResults();
    }

    if (!vm.__tileclubInstantSearchPolicyInstalled) {
      vm.__tileclubInstantSearchPolicyInstalled = true;

      vm.updateTerm = function (value) {
        vm.term = value;
        syncInputDisplay(vm, value);
        cancelPending(vm);

        var t = trimTerm(value);
        if (!t.length) {
          setInstantSearchTypingPending(vm, false);
          origUpdateResults();
          return;
        }

        setInstantSearchTypingPending(vm, true);

        var delay = t.length < MIN_CHARS_FOR_FAST_DEBOUNCE ? SHORT_QUERY_DEBOUNCE_MS : LONG_QUERY_DEBOUNCE_MS;
        vm.__tileclubSearchTimer = setTimeout(function () {
          vm.__tileclubSearchTimer = null;
          origUpdateResults();
        }, delay);
      };

      if (typeof vm.$watch === 'function' && !vm.__tileclubLoaderWatchInstalled) {
        vm.__tileclubLoaderWatchInstalled = true;
        vm.$watch('loader', function (val) {
          if (val !== false || !vm.__tileclubInstantSearchTypingPending) return;
          if (vm.__tileclubSearchTimer != null) return;
          var r = vm.result;
          if (r && r.query != null && refineForCompare(vm.term) === refineForCompare(r.query)) return;
          setInstantSearchTypingPending(vm, false);
        });
      }
    }

    function bindInputListeners() {
      var el = vm.$refs && vm.$refs.searchInput;
      if (!el || el.__tileclubUsfBlurBound) return;
      el.__tileclubUsfBlurBound = true;

      el.addEventListener(
        'blur',
        function () {
          flushUpdateResults();
        },
        true
      );

      el.addEventListener('compositionend', function () {
        if (typeof vm.updateTerm === 'function') vm.updateTerm(el.value);
      });
    }

    if (typeof vm.$nextTick === 'function') {
      vm.$nextTick(function () {
        bindInputListeners();
      });
    } else {
      bindInputListeners();
    }
  }

  function installInstantSearchInputPolicy() {
    if (!usf.settings || !usf.settings.instantSearch || !usf.settings.instantSearch.online) return;
    var vm = usf.instantSearch;
    if (!vm) return;
    installOnInstance(vm);
  }

  function hookGlobalEventsOnce() {
    if (globalEventsHooked) return;
    globalEventsHooked = true;
    usf.event.add('init', installInstantSearchInputPolicy);
    usf.event.add('is_show', installInstantSearchInputPolicy);
    usf.event.add('is_updated', function (inst) {
      maybeClearInstantSearchTypingPending(inst);
    });
    usf.event.add('is_hide', function () {
      var vm = usf.instantSearch;
      if (vm) setInstantSearchTypingPending(vm, false);
    });
  }

  function whenUsfReady(cb) {
    if (window.usf && usf.event && typeof usf.event.add === 'function') {
      cb();
      return;
    }
    setTimeout(function () {
      whenUsfReady(cb);
    }, 25);
  }

  whenUsfReady(function () {
    hookGlobalEventsOnce();
    installInstantSearchInputPolicy();
  });
})();
